import React, { useContext, useEffect, useState } from "react";
import {
  PlusOutlined,
  DeleteOutlined,
  MoreOutlined,
  UserOutlined,
} from "@ant-design/icons";
import GenericPageManager from "../../../Components/PageManager/GenericPageManager";
import { Dropdown, message } from "antd";
import SettingUserIcon from "../../../Assets/Icons/settings_user.svg";
import AdminPanelIcon from "../../../Assets/Icons/admin_panel_settings.svg";
import GenericModalForm from "../../../Components/Modals/GenericModalForm";
import GenericModal from "../../../Components/Modals/GenericModal";
import { UserContext } from "../../../App";
import { handleUpdateUser } from "../../../Utlis/UpdateUserState";
import {
  getRoles,
  createRole,
  deleteRole,
  updateRolePageAccess,
} from "../../../API/FleetManager/SettingsPermissions";
import { getManagerProfile } from "../../../API/FleetManager/MyProfile";
import usePageAccess from "../../../Utlis/usePageAccess";

const PAGE_SLUGS = [
  "dashboard",
  "vehicles",
  "drivers",
  "fuel-vendors",
  "credits-billing",
  "subscription-payments",
  "transactions",
  "reports",
  "user-management",
  "settings-permissions",
  "audit-trail",
  "company-details",
];

const formatPageName = (key) =>
  key
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const toSlug = (str) =>
  str
    ?.toLowerCase()
    .trim()
    .replace(/\s+/g, "-");

const pagesList = PAGE_SLUGS.map(formatPageName);

const defaultAccess = {};
pagesList.forEach((page) => {
  defaultAccess[page] = "restrict";
});

const mapRole = (item, index) => {
  const rawPageAccess = item.page_access || {};

  return {
    id: item.id,
    name: item.name,
    subtitle: item.description || item.subtitle || "",
    active: index === 0,
    icon: index === 0 ? AdminPanelIcon : SettingUserIcon,
    pageAccess: {
      ...defaultAccess,
      ...Object.fromEntries(
        Object.entries(rawPageAccess).map(([key, value]) => [
          formatPageName(key),
          value,
        ]),
      ),
    },
  };
};

const SettingsPermissions = () => {
  const { canEdit } = usePageAccess("settings-permissions");
  const { userData, setUserData } = useContext(UserContext);
  const [selectedRoleIndex, setSelectedRoleIndex] = useState();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [roles, setRoles] = useState([]);
  const [ownRoleName, setOwnRoleName] = useState(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await getRoles();
        const rolesData = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(rolesData)) {
          setRoles(rolesData.map(mapRole));
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load roles."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load roles."),
        );
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    // userData.role isn't populated at login, so fetch the logged-in
    // manager's own role name from their profile to compare against the
    // role being edited (for the sidebar self-sync below).
    const fetchOwnRole = async () => {
      try {
        const res = await getManagerProfile();

        if (Math.floor(res?.status / 100) === 2) {
          setOwnRoleName(res?.data?.data?.role || null);
        }
      } catch (err) {
        console.error("Failed to fetch own role:", err);
      }
    };

    fetchOwnRole();
  }, []);

  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);

  const activeRole = roles.find((role) => role.active);

  const handleAddRole = async (values) => {
    const rawPageAccess = Object.fromEntries(
      PAGE_SLUGS.map((slug) => [slug, "restrict"]),
    );

    try {
      const res = await createRole({
        name: values.roleTitle,
        subtitle: values.roleSubtitle,
        page_access: rawPageAccess,
      });

      if (Math.floor(res?.status / 100) === 2) {
        const created = mapRole(res?.data?.data || {}, roles.length);

        setRoles([
          ...roles.map((r) => ({ ...r, active: false })),
          { ...created, active: true },
        ]);
        message.success(
          getErrorMessage(res?.data, "Role created successfully"),
        );
        setIsAddRoleOpen(false);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to create role."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to create role."),
      );
    }
  };

  const handleDeleteClick = (e, index) => {
    e.stopPropagation();
    setOpenDropdownIndex(null);
    setSelectedRoleIndex(index);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedRoleIndex === null) return;

    const roleToDelete = roles[selectedRoleIndex];

    try {
      const res = await deleteRole(roleToDelete.id);

      if (Math.floor(res?.status / 100) === 2) {
        const updatedRoles = [...roles];
        const wasActive = updatedRoles[selectedRoleIndex].active;

        updatedRoles.splice(selectedRoleIndex, 1);

        if (wasActive && updatedRoles.length > 0) {
          updatedRoles[0].active = true;
        }

        setRoles(updatedRoles);
        message.success(
          getErrorMessage(res?.data, "Role deleted successfully"),
        );
      } else {
        message.error(getErrorMessage(res?.data, "Failed to delete role."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete role."),
      );
    }

    setIsDeleteModalOpen(false);
    setSelectedRoleIndex(null);
  };

  const handleRoleClick = (index) => {
    setRoles(roles.map((role, i) => ({ ...role, active: i === index })));
  };

  const handlePageAccessChange = async (newAccess) => {
    setRoles(
      roles.map((role) =>
        role.active ? { ...role, pageAccess: newAccess } : role,
      ),
    );

    if (!activeRole?.id) return;

    const rawPageAccess = Object.fromEntries(
      Object.entries(newAccess).map(([key, value]) => [toSlug(key), value]),
    );

    try {
      const res = await updateRolePageAccess(activeRole.id, rawPageAccess);

      if (Math.floor(res?.status / 100) === 2) {
        // If the role being edited is the logged-in manager's own role, push
        // the new access into UserContext so the sidebar updates instantly
        // instead of requiring a re-login. userData.role isn't populated at
        // login, so ownRoleName (fetched from the profile endpoint) is used
        // instead. Compared loosely (trimmed, case-insensitive).
        const isOwnRole =
          ownRoleName?.trim().toLowerCase() ===
          activeRole.name?.trim().toLowerCase();

        if (isOwnRole) {
          handleUpdateUser(setUserData, {
            ...userData,
            page_access: rawPageAccess,
          });
        }

        message.success(
          getErrorMessage(res?.data, "Page access updated successfully"),
        );
      } else {
        message.error(
          getErrorMessage(res?.data, "Unexpected response from server."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update page access."),
      );
    }
  };

  return (
    <div className="settings-permission-container">
      <div className="settings-permission-header">Settings & Permissions</div>

      <div className="settings-permission-body">
        {/* LEFT COLUMN */}
        <div className="left-column">
          <div className="left-column-header">
            <div>
              <div className="left-column-header-title">System Role</div>
              <div className="left-column-header-subtitle">
                Select a role configure access
              </div>
            </div>
            {canEdit && (
              <div
                className="plus-icon-container"
                onClick={() => setIsAddRoleOpen(true)}
              >
                <PlusOutlined className="plus-icon" />
              </div>
            )}
          </div>

          {/* Roles List */}
          <div className="role-list">
            {roles.map((role, index) => {
              const menuItems = [
                {
                  key: "delete",
                  icon: <DeleteOutlined className="delete-menu-item" />,
                  label: (
                    <span
                      className="delete-menu-item"
                      onClick={(e) => handleDeleteClick(e, index)}
                    >
                      Delete Role
                    </span>
                  ),
                },
              ];

              return (
                <div
                  key={index}
                  className={`role-item ${role.active ? "active" : ""}`}
                  onClick={() => handleRoleClick(index)}
                >
                  {/* LEFT SIDE */}
                  <div className="role-left">
                    <div className="role-icon">
                      <img src={role.icon} alt="Role Icon" />
                    </div>

                    <div className="role-text">
                      <div className="role-name">{role.name}</div>
                      <div className="role-subtitle">{role.subtitle}</div>
                    </div>
                  </div>

                  {/* RIGHT MENU */}
                  {canEdit && (
                    <Dropdown
                      menu={{ items: menuItems }}
                      trigger={["click"]}
                      placement="bottomRight"
                      open={openDropdownIndex === index}
                      onOpenChange={(open) => {
                        setOpenDropdownIndex(open ? index : null);
                      }}
                    >
                      <div
                        className="role-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreOutlined
                          style={{ fontSize: "25px" }}
                          size="lg"
                          className="role-menu-icon"
                        />
                      </div>
                    </Dropdown>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {/* RIGHT COLUMN */}
        <div className="right-column">
          <div className="right-column-title">Operations Permissions</div>
          {activeRole && (
            <GenericPageManager
              pagesList={pagesList}
              pageAccess={activeRole.pageAccess}
              setPageAccess={handlePageAccessChange}
              isCollapsible={false}
              readOnly={!canEdit}
              readOnlyTooltip={{
                title: "View only access",
                text: "You only have view rights on Settings & Permissions, so you can't change these permissions.",
              }}
            />
          )}
        </div>
      </div>

      <GenericModal
        open={isDeleteModalOpen}
        title="Delete Role"
        subtitle={`Are you sure you want to delete ${
          selectedRoleIndex !== null ? roles[selectedRoleIndex]?.name : ""
        } Role?`}
        highlightText={
          selectedRoleIndex !== null ? roles[selectedRoleIndex]?.name : ""
        }
        showReason={false}
        confirmText="Delete"
        cancelText="Cancel"
        iconColor="#DC2626"
        iconBgColor="#FEE2E2"
        confirmBtnColor="#DC2626"
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setSelectedRoleIndex(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <GenericModalForm
        key={isAddRoleOpen ? "open" : "closed"}
        open={isAddRoleOpen}
        title="Add New Role"
        subtitle="Create a new Role"
        okText="Add Role"
        fields={[
          {
            name: "roleTitle",
            label: "Role Title",
            required: true,
            placeholder: "Enter role title",
            type: "input",
            rules: [{ required: true, message: "Please enter your role!" }],
          },
          {
            name: "roleSubtitle",
            label: "Role Subtitle",
            required: true,
            placeholder: "Enter role subtitle",
            type: "input",
            rules: [{ required: true, message: "Please enter sub title" }],
          },
        ]}
        cancelText="Cancel"
        onCancel={() => setIsAddRoleOpen(false)}
        onOk={handleAddRole}
      />
    </div>
  );
};

export default SettingsPermissions;
